/**
 * Element picker — el "inspeccionar" del navegador, pero del SDK. Corre
 * DENTRO de la app del cliente (no en el dashboard: son orígenes distintos
 * y el dashboard no puede leer el DOM de la app). Resalta el elemento bajo
 * el cursor, muestra su selector + dimensiones, y al hacer click captura un
 * selector CSS estable reutilizando `buildSelectorPath` del autocapture.
 *
 * Es la base del builder no-code: el dashboard abre la app del cliente en
 * modo builder, el usuario "pincha" un elemento y el selector resultante se
 * usa como ancla de un tooltip/walkthrough. El puente con el dashboard
 * (postMessage) es la fase siguiente; aquí exponemos el primitivo
 * `startElementPicker(onPick)`.
 *
 * No usa `innerHTML` — todo por `textContent`/DOM, igual que los renderers.
 */

import { hasDocument } from '../../utils/safe-env';
import { buildSelectorPath, filterHumanClasses } from '../autocapture/selector-builder';

/** z-index por encima de las guías (2147483640) para tapar también una preview. */
const PICKER_Z = 2147483646;
const PICKER_HOST_ATTR = 'data-veo-picker';
const DEFAULT_MAX_ANCESTORS = 5;

/** Datos del elemento elegido que se entregan al caller. */
export interface PickedElement {
  /** Selector CSS estable con ancestros (lo que se guarda como ancla). */
  selector: string;
  /** Selector corto solo del elemento (tag + id/atributo/clases). */
  shortSelector: string;
  tag: string;
  id: string | null;
  classes: string[];
  text: string;
  rect: { x: number; y: number; width: number; height: number };
}

export interface ElementPickerOptions {
  /** Llamado al hacer click en un elemento. El picker se cierra tras esto. */
  onPick: (picked: PickedElement) => void;
  /** Llamado al cancelar (Esc). El picker se cierra. */
  onCancel?: () => void;
  /** Niveles de ancestros del selector. Default 5 (igual que autocapture). */
  maxAncestors?: number;
  /** Texto del banner superior. Default en español. */
  hint?: string;
}

export interface ElementPickerHandle {
  /** Cierra el picker sin elegir nada. Idempotente. */
  stop(): void;
}

const PICKER_STYLES = `
:host { all: initial; }
.veo-pick-box {
  position: fixed;
  z-index: ${PICKER_Z};
  pointer-events: none;
  box-sizing: border-box;
  background: rgba(99, 102, 241, 0.22);
  border: 1px dashed rgba(99, 102, 241, 0.95);
  border-radius: 2px;
  transition: top 60ms ease-out, left 60ms ease-out, width 60ms ease-out, height 60ms ease-out;
}
.veo-pick-label {
  position: fixed;
  z-index: ${PICKER_Z};
  pointer-events: none;
  max-width: 360px;
  background: #1e1b4b;
  color: #e0e7ff;
  font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace;
  padding: 6px 9px;
  border-radius: 6px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
}
.veo-pick-label .veo-pick-sel { color: #c7d2fe; word-break: break-all; }
.veo-pick-label .veo-pick-dim { color: #a5b4fc; margin-top: 3px; }
.veo-pick-hint {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${PICKER_Z};
  pointer-events: none;
  background: #4338ca;
  color: #fff;
  font: 13px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 8px 14px;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}
`;

/**
 * Controla un picker activo. Singleton a nivel de módulo: empezar otro
 * cierra el anterior. Los listeners van en fase de captura para poder
 * cancelar el click ANTES de que la app del cliente reaccione.
 */
class ElementPicker {
  private host: HTMLElement | null = null;
  private box: HTMLElement | null = null;
  private label: HTMLElement | null = null;
  private current: Element | null = null;
  private maxAncestors = DEFAULT_MAX_ANCESTORS;
  private onPick: ((p: PickedElement) => void) | null = null;
  private onCancel: (() => void) | null = null;

  start(options: ElementPickerOptions): void {
    this.stop(); // solo un picker a la vez

    this.maxAncestors = options.maxAncestors ?? DEFAULT_MAX_ANCESTORS;
    this.onPick = options.onPick;
    this.onCancel = options.onCancel ?? null;

    const host = document.createElement('div');
    host.setAttribute(PICKER_HOST_ATTR, '');
    const shadow = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = PICKER_STYLES;

    this.box = document.createElement('div');
    this.box.className = 'veo-pick-box';
    this.box.style.display = 'none';

    this.label = document.createElement('div');
    this.label.className = 'veo-pick-label';
    this.label.style.display = 'none';

    const hint = document.createElement('div');
    hint.className = 'veo-pick-hint';
    hint.textContent = options.hint ?? 'Selecciona un elemento · Esc para cancelar';

    shadow.append(style, this.box, this.label, hint);
    document.body.appendChild(host);
    this.host = host;

    document.addEventListener('mousemove', this.onMove, true);
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('mousedown', this.swallow, true);
    document.addEventListener('mouseup', this.swallow, true);
    document.addEventListener('keydown', this.onKey, true);
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  stop(): void {
    document.removeEventListener('mousemove', this.onMove, true);
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('mousedown', this.swallow, true);
    document.removeEventListener('mouseup', this.swallow, true);
    document.removeEventListener('keydown', this.onKey, true);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);

    if (this.host?.parentNode) this.host.parentNode.removeChild(this.host);
    this.host = null;
    this.box = null;
    this.label = null;
    this.current = null;
    this.onPick = null;
    this.onCancel = null;
  }

  /** ¿Es un nodo de nuestro propio overlay? (no debe ser seleccionable). */
  private isOwn(target: EventTarget | null): boolean {
    return target instanceof Node && this.host !== null && this.host.contains(target);
  }

  private onMove = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element) || this.isOwn(target)) return;
    this.current = target;
    this.draw(target);
  };

  private onClick = (event: MouseEvent): void => {
    const target = this.current ?? (event.target instanceof Element ? event.target : null);
    if (!target || this.isOwn(target)) return;

    // Evitar que la app reaccione al click de selección.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const picked = this.describe(target);
    const cb = this.onPick;
    this.stop();
    cb?.(picked);
  };

  private onKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    const cb = this.onCancel;
    this.stop();
    cb?.();
  };

  /** Traga mousedown/mouseup durante el picking (evita focus/active de la app). */
  private swallow = (event: MouseEvent): void => {
    if (this.isOwn(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  private reposition = (): void => {
    if (this.current) this.draw(this.current);
  };

  /** Dibuja el recuadro de resalte y la etiqueta sobre el elemento dado. */
  private draw(element: Element): void {
    if (!this.box || !this.label) return;
    const rect = element.getBoundingClientRect();

    this.box.style.display = 'block';
    this.box.style.top = `${rect.top}px`;
    this.box.style.left = `${rect.left}px`;
    this.box.style.width = `${rect.width}px`;
    this.box.style.height = `${rect.height}px`;

    const shortSel = buildSelectorPath(element, 0);
    this.label.textContent = '';
    const sel = document.createElement('div');
    sel.className = 'veo-pick-sel';
    sel.textContent = shortSel;
    const dim = document.createElement('div');
    dim.className = 'veo-pick-dim';
    dim.textContent = `${Math.round(rect.width)} × ${Math.round(rect.height)}`;
    this.label.append(sel, dim);

    // Etiqueta debajo del recuadro; si no hay espacio, arriba.
    this.label.style.display = 'block';
    const LABEL_H = 48;
    const placeAbove = rect.bottom + 8 + LABEL_H > window.innerHeight;
    this.label.style.top = placeAbove
      ? `${Math.max(8, rect.top - LABEL_H - 8)}px`
      : `${rect.bottom + 8}px`;
    this.label.style.left = `${Math.max(8, rect.left)}px`;
  }

  private describe(element: Element): PickedElement {
    const rect = element.getBoundingClientRect();
    return {
      selector: buildSelectorPath(element, this.maxAncestors),
      shortSelector: buildSelectorPath(element, 0),
      tag: element.tagName.toLowerCase(),
      id: element.id || null,
      classes: filterHumanClasses(element),
      text: (element.textContent ?? '').trim().slice(0, 120),
      rect: {
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      },
    };
  }
}

let activePicker: ElementPicker | null = null;

/**
 * Inicia el modo de selección de elementos (inspector). Resalta lo que está
 * bajo el cursor y, al hacer click, entrega un selector CSS estable.
 * Devuelve un handle con `stop()`. Iniciar otro picker cierra el anterior.
 *
 * @example
 * ```ts
 * import { startElementPicker } from '@veo/sdk';
 *
 * const picker = startElementPicker({
 *   onPick: ({ selector }) => console.log('elegido:', selector),
 *   onCancel: () => console.log('cancelado'),
 * });
 * // picker.stop() para abortar manualmente.
 * ```
 */
export function startElementPicker(options: ElementPickerOptions): ElementPickerHandle {
  if (!hasDocument()) {
    return { stop: () => {} };
  }
  if (!activePicker) activePicker = new ElementPicker();
  activePicker.start(options);
  return { stop: () => activePicker?.stop() };
}

/** Cierra el picker activo, si lo hay. Idempotente. */
export function stopElementPicker(): void {
  activePicker?.stop();
}
